import { useSharedState, useUpdateSharedState } from '@/hooks/state/shared-state';
import { clientErrorLoggerApiService } from '@/services/(frontend)/client-error-logger-api.service';
import { isValidLatLng } from '@/utils/location';
import { navigateTo } from '@/utils/navigation';
import { PagedListOfTheater } from '@cinemark/content-api-client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useUpdateTheaterContext } from './state/theater';
import { useUpdateTheatersContext } from './state/theaters';
import { INVALID_LAT_LNG, useTheatersByGeoLocation } from './theater/theaters';

type UseGeoTheaterSearchByType = 'search-and-redirect' | 'return-results' | 'set-location';

type SearchByGeoResponseType = {
  data: PagedListOfTheater;
  error?: GeolocationPositionError;
};

type GeoLocationType = {
  latitude: number;
  longitude: number;
};

type UseGeoTheaterSearchType = {
  searchByGeo: (type?: UseGeoTheaterSearchByType, geoLocation?: GeoLocationType) => Promise<SearchByGeoResponseType>;
};

const baseResults: PagedListOfTheater = {
  data: [],
  hasNextPage: false,
  hasPreviousPage: false,
  pageCount: 0,
  pageNumber: 0,
  pageSize: 0,
  totalItemCount: 0,
};

const nearbyTheatersUrl = '/theatres?tab=nearby';

export const useGeoTheaterSearch = (): UseGeoTheaterSearchType => {
  const [geoLocation, setGeoLocation] = useState<{ latitude?: number; longitude?: number; }>();
  const [geoSearchType, setGeoSearchType] = useState<UseGeoTheaterSearchByType>();
  const pendingPromise = useRef<{ resolve: (value: SearchByGeoResponseType) => void; } | null>(null);

  const { data: sharedState } = useSharedState();
  const { mutateAsync: updateSharedState } = useUpdateSharedState();
  const { data: theaters, isError, isLoading } = useTheatersByGeoLocation(geoLocation?.latitude ?? INVALID_LAT_LNG, geoLocation?.longitude ?? INVALID_LAT_LNG);
  const { mutateAsync: updateTheaterContext } = useUpdateTheaterContext();
  const { mutateAsync: updateTheatersContext } = useUpdateTheatersContext();

  useEffect(() => {
    const updateTheaterData = async (): Promise<void> => {
      if (!pendingPromise.current) {
        return;
      }

      if (isError) {
        const mockError = {
          code: 0, // this is an arbitrary code for UNKNOWN_ERROR
          message: 'Failed to retrieve geolocation',
          /* eslint-disable @typescript-eslint/naming-convention */
          PERMISSION_DENIED: 1,
          POSITION_UNAVAILABLE: 2,
          TIMEOUT: 3,
          /* eslint-enable @typescript-eslint/naming-convention */
        } as GeolocationPositionError;

        pendingPromise.current.resolve({ data: baseResults, error: mockError });
        pendingPromise.current = null;
        return;
      }

      if (geoSearchType === 'return-results') {
        pendingPromise.current.resolve({ data: theaters ?? baseResults });
        pendingPromise.current = null;
        if (geoLocation) {
          await updateSharedState({ lastGeoPosition: `${geoLocation.latitude},${geoLocation.longitude}` });
        }
        return;
      }

      if (theaters?.data?.length) {
        const firstTheater = theaters.data[0];

        updateTheaterContext(firstTheater);
        updateTheatersContext(theaters);

        await updateSharedState({
          lastGeoPosition: geoLocation ? `${geoLocation.latitude},${geoLocation.longitude}` : null,
          lastTextSearch: null,
          lastViewedTheaterId: firstTheater.theaterId,
          lastViewedTheaterUrl: geoSearchType === 'search-and-redirect' ? nearbyTheatersUrl : sharedState?.lastViewedTheaterUrl,
        });

        if (geoSearchType === 'search-and-redirect') {
          navigateTo(nearbyTheatersUrl);
        }
      }

      pendingPromise.current.resolve({ data: baseResults });
      pendingPromise.current = null;
    };

    if (!isLoading) {
      updateTheaterData();
    }
  }, [geoLocation, geoSearchType, isError, isLoading, sharedState?.lastViewedTheaterUrl,
    theaters, updateSharedState, updateTheaterContext, updateTheatersContext]);

  const searchByGeo = useCallback(async (type?: UseGeoTheaterSearchByType, geoLocation?: GeoLocationType): Promise<SearchByGeoResponseType> => {
    try {
      let newGeoLocation = geoLocation;
      if (!newGeoLocation || !isValidLatLng(newGeoLocation.latitude, newGeoLocation.longitude)) {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        });
        newGeoLocation = { latitude: position.coords.latitude, longitude: position.coords.longitude };
      }

      setGeoSearchType(type ?? 'search-and-redirect');
      setGeoLocation(newGeoLocation);

      return new Promise(resolve => pendingPromise.current = { resolve });
    } catch (error) {
      void clientErrorLoggerApiService.logError(error);
      return error instanceof GeolocationPositionError ? { data: baseResults, error } : { data: baseResults };
    }
  }, []);

  return { searchByGeo };
};
